<!-- One event's attendees on the year's People page (fullstack claude/ATTENDEES-SPEC.md): name,
     affiliation, recent activity (green: in the last 5 minutes; orange: the last 30; grey:
     neither) and an email button, enabled where the attendee lets others contact them. The
     row, or its button, opens the Send Email dialog (App/Modal/ContactAttendee.vue, with the
     other dialogs) by a pushed hash, so Back closes it and the page's filter stays. Never an
     email address. -->

<template>
  <section :id="label.replace(/ /g, '-')" class="attendees" :aria-labelledby="headingId">
    <!-- the heading, and on its line, right-aligned, the email column's heading (Seán,
         2026-11-03) -->
    <div class="heading">
      <h4 :id="headingId">
        {{ title }}
        <AppHelpPopup :id="`${headingId}-who`" :label="`Who can see the ${label}`" class="help">
          <span class="helpParagraph">
            This list is only visible to people registered for the VIZBI {{ year }}
            <i>Online</i> {{ eventTitle }}.
          </span>
        </AppHelpPopup>
      </h4>
      <!-- staff only: the lists again, as people register and join (Seán, 2026-11-05) -->
      <button
        v-if="isStaffView"
        type="button"
        class="reload"
        :class="{isReloading}"
        aria-label="Reload"
        title="Reload"
        :disabled="isReloading"
        @click="reloadAttendees(Number(year))">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 12a8 8 0 1 1-2.34-5.66" />
          <path d="M20 4v5h-5" />
        </svg>
      </button>
      <!-- the email column's heading, with the standard "?" (App/HelpPopup.vue; Seán,
           2026-11-03): what forwarding means, and where to change one's own setting -->
      <span v-if="hasList" class="legend">
        Email
        <AppHelpPopup :id="`${headingId}-forwarding`" label="About emailing attendees" class="help">
          <span class="helpParagraph">
            VIZBI can send an email on your behalf to attendees who want to be contacted.
          </span>
          <span class="helpParagraph">
            Update <router-link :to="{path: route.path, hash: '#profile'}">your profile</router-link>
            to change your contact preferences.
          </span>
        </AppHelpPopup>
      </span>
    </div>
    <!-- a list the viewer can't see: why, in one line (Seán, 2026-11-03), never its rows -->
    <!-- waiting for Clerk or the server (Seán, 2026-11-04): placeholder rows, gently moving, so
         the section never looks empty by mistake; never a name until the server sends it -->
    <div v-if="isWaiting" class="waiting" role="status" aria-live="polite">
      <p class="waitingText">Fetching attendees…</p>
      <ul class="rows" aria-hidden="true">
        <li v-for="width in PLACEHOLDER_WIDTHS" :key="width" class="row placeholder">
          <span class="dot" />
          <span class="bar" :style="{width: `${width}%`}" />
          <span class="bar" :style="{width: `${110 - width}%`}" />
          <span />
        </li>
      </ul>
    </div>
    <p v-else-if="!hasList" class="closedNote">
      <template v-if="!isSignedIn">
        This list is shared only with the people who joined the event.
        <router-link :to="{path: route.path, hash: '#sign-in'}">Sign in</router-link> to see it.
      </template>
      <template v-else-if="closed?.reason === 'not-joined'">
        This list is shared with the people who joined the meeting. It opens to you once you
        join it.
      </template>
      <template v-else-if="closed?.reason === 'not-open'">
        This list opens {{ opensWhen }}.
      </template>
      <template v-else>
        This list is shared only with people registered for the VIZBI {{ year }} <i>Online</i>
        {{ eventTitle }}.
      </template>
    </p>
    <!-- admins, editors and testers (Seán, 2026-11-05), sent to them only: before anyone joins,
         the registrants alone (the title says Registrants); then the attendees, and below them
         the Other Registrants -->
    <template v-for="group in groups" v-else :key="group.key">
      <h5 v-if="group.label" class="groupHeading">{{ group.label }}</h5>
      <ul class="rows">
      <li
        v-for="row in group.rows"
        :key="row.id"
        class="row"
        :class="{isContactable: row.isContactable}"
        @click="onRowClick(row, $event)">
        <span class="dot" :class="getPresence(row.id)" :title="PRESENCE_TEXT[getPresence(row.id)]">
          <span class="visuallyHidden">{{ PRESENCE_TEXT[getPresence(row.id)] }}</span>
        </span>
        <span class="name">{{ getName(row) }}</span>
        <span class="affiliation">{{ row.affiliation }}</span>
        <span class="contact">
          <button
            v-if="!row.isSelf && group.key !== 'others'"
            type="button"
            class="emailButton"
            :aria-disabled="String(!row.isContactable)"
            :aria-label="row.isContactable ? `Email ${getName(row)}` : NOT_REACHABLE"
            :title="row.isContactable ? `Email ${getName(row)}` : NOT_REACHABLE"
            @click="openContact(row)">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="2.5" y="5" width="19" height="14" rx="2" />
              <path d="M3 6.5l9 6.5 9-6.5" />
              <!-- not contactable: the envelope struck through, "no" -->
              <path v-if="!row.isContactable" class="strike" d="M3 3l18 18" />
            </svg>
          </button>
        </span>
      </li>
      </ul>
    </template>
  </section>
</template>

<script setup>
import {computed, useId} from "vue";
import {useRoute, useRouter} from "vue-router";
import {
  closedReasons, directories, isReloading, isStaffView, loadedYear, otherRegistrants, presence,
  reloadAttendees,
} from "@/composables/useAttendees";
import useUserSafe from "@/composables/useUserSafe";
import AppHelpPopup from "@/components/App/HelpPopup.vue";

const props = defineProps({
  kind: {type: String, required: true},
  label: {type: String, required: true},
  sort: {type: String, default: ""}
});

const NOT_REACHABLE = "This user has chosen not to be reachable by email.";
const PLACEHOLDER_WIDTHS = [62, 48, 70, 55, 66, 44]; // the placeholder rows' name bars (%)
const PRESENCE_TEXT = {
  active: "Active in the last 5 minutes",
  recent: "Active in the last 30 minutes",
  inactive: "Not active recently"
};

function getName(row) {
  return [row.firstName, row.lastName].filter(Boolean).join(" ");
}

function getSortKey(row, isByFirstName) {
  const [first, last] = [row.firstName || "", row.lastName || ""];
  return (isByFirstName ? `${first} ${last}` : `${last} ${first}`).toLocaleLowerCase();
}

const [route, router] = [useRoute(), useRouter()];
const year = computed(() => route.params.year);
const {isSignedIn, isLoaded: isUserLoaded} = useUserSafe();
const hasList = computed(() => Array.isArray(directories.value[props.kind]));
const closed = computed(() => closedReasons.value[props.kind]);
// signed in: the server's answer in, so a line says why only once it's known (no flash of one)
const isLoaded = computed(() => loadedYear.value === Number(year.value));
// nothing to show yet: who the viewer is, or (signed in) the server's answer, still coming
const isWaiting = computed(() => !isUserLoaded.value || (isSignedIn.value && !isLoaded.value));
// "on 17 November, a day before the Workshop starts" (the date in the viewer's time zone)
const opensWhen = computed(() => {
  const opensAt = closed.value?.opensAt;
  const before = `a day before the ${eventTitle.value} starts`;
  if (!opensAt) return before;
  const day = new Date(opensAt).toLocaleDateString("en-GB", {day: "numeric", month: "long"});
  return `on ${day}, ${before}`;
});
const eventTitle = computed(() => (props.kind === "workshop" ? "Workshop" : "Masterclass"));
const headingId = useId();

function getSorted(list, isByFirstName) {
  return [...(list || [])].sort((first, second) => {
    return getSortKey(first, isByFirstName).localeCompare(getSortKey(second, isByFirstName));
  });
}

// staff, before anyone has joined: the section holds only its registrants, and says so in its
// title ("Online Workshop Registrants"); the label (its address, the filter) stays
const isRegistrantsOnly = computed(() => {
  return isStaffView.value && !(directories.value[props.kind] || []).length;
});
const title = computed(() => {
  return isRegistrantsOnly.value ? props.label.replace(/Attendees$/, "Registrants") : props.label;
});

// the attendees (those who joined), then, for staff only, the other registrants
const groups = computed(() => {
  const isByFirstName = props.sort === "First name";
  const others = getSorted(otherRegistrants.value[props.kind], isByFirstName);
  const attended = {key: "attended", label: "",
    rows: getSorted(directories.value[props.kind], isByFirstName)};
  if (!isStaffView.value) return [attended];
  if (!attended.rows.length) return [{key: "others", label: "", rows: others}];
  if (!others.length) return [attended];
  return [attended, {key: "others", label: "Other Registrants", rows: others}];
});

const getPresence = (id) => presence.value[id] || "inactive";

// the whole row opens the dialog, as its button does (Seán, 2026-11-03: easier to find); the
// button stays the keyboard's way in, and its own click isn't counted twice
function onRowClick(row, event) {
  if (event.target.closest("button")) return;
  openContact(row);
}

// a push, so Back closes the dialog; the filter's fragment (written by People.vue outside the
// router) goes with it, for the dialog to restore on closing
function openContact(row) {
  if (!row.isContactable) return;
  // always last, so the address never ends in the id: an "_" there means a person (Highlight.vue)
  const filter = window.location.hash.replace(/^#/, "");
  const from = `&from=${filter.startsWith("contact-attendee") ? "" : filter}`;
  router.push({path: route.path, hash: `#contact-attendee&${props.kind}&${row.id}${from}`});
}
</script>

<style scoped>
.attendees {
  --presence-active: var(--brand-color);
  --presence-recent: var(--orange-on-light, #d7610c);
  --presence-inactive: color-mix(in srgb, var(--text-color) 25%, transparent); /* strongly muted */
  /* the email button (Seán, 2026-11-03): available, the text's own colour (the names'), not a
     green; not available, that colour faded, clearly inactive, on either theme */
  --email-available: var(--text-color);
  /* Apple's bordered table (macOS HIG, Lists and tables): full-width, square rows, every other
     one a faint tint; compact rows, as macOS's, taller for a finger (Seán, 2026-11-03) */
  --row-height: 32px;
  --stripe: color-mix(in srgb, var(--text-color) 4%, transparent);
  --row-hover: color-mix(in srgb, var(--text-color) 10%, transparent); /* plainly not a stripe */
  --email-unavailable: color-mix(in srgb, var(--text-color) 40%, transparent); /* the slash legible */
  margin-bottom: 32px; /* ▶ EDIT HERE: space below each attendee section */
  text-align: left;
}
.heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  column-gap: 16px;
}
/* the two "?"s alike (Seán, 2026-11-03): the height of the heading's capitals (1.2rem type:
   0.86rem), on the baseline as a capital sits; their text not the heading's bold */
.help {
  --cap-height: 0.86rem; /* ▶ EDIT HERE: with the heading's size */
  vertical-align: baseline;
  font-weight: normal;
}
.help :deep(.helpButton) {
  display: block;
  width: var(--cap-height);
  height: var(--cap-height);
  font-size: calc(var(--cap-height) * 0.85);
}
.reload {
  align-self: center;
  width: 28px;
  height: 28px;
  margin-left: 8px; /* ▶ EDIT HERE: space between the heading and the reload button */
  padding: 4px;
  border: none;
  border-radius: 50%;
  box-shadow: none;
  background: none;
  color: var(--text-color-muted, #6a6a6a);
  cursor: pointer;
}
.reload:hover:not(:disabled) {
  background: var(--row-hover);
}
.reload svg {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.reload.isReloading svg {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .reload.isReloading svg {
    animation: none;
  }
}
.legend {
  margin-left: auto; /* right-aligned, on a phone too, where it wraps below the heading */
  margin-bottom: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-right: 8px; /* its end in line with the rows' (their padding) */
  font-size: 0.95rem; /* ▶ EDIT HERE: the column heading's size */
  color: var(--text-color-muted, #6a6a6a);
}
.closedNote {
  margin: 0 0 8px;
  padding: 8px;
  text-align: left;
  color: var(--text-color-muted, #6a6a6a);
  background-color: var(--stripe);
}
/* a link in the note's text: underlined, not told apart by colour alone (WCAG 1.4.1) */
.closedNote a {
  text-decoration: underline;
  text-underline-offset: 0.15em;
}
/* the placeholder rows: the list's own rows, a bar for each text, a light sweeping across */
.waitingText {
  margin: 0 0 8px;
  color: var(--text-color-muted, #6a6a6a);
}
.bar {
  height: 0.7em;
  max-width: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--stripe) 0%, var(--row-hover) 50%, var(--stripe) 100%);
  background-size: 200% 100%;
  animation: sweep 1.4s ease-in-out infinite;
}
@keyframes sweep {
  from {
    background-position: 100% 0;
  }
  to {
    background-position: -100% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .bar {
    animation: none;
    background: var(--row-hover);
  }
}
.groupHeading {
  margin: 0 0 0.5rem; /* ▶ EDIT HERE: space under "Other Registrants" */
  padding-top: 1rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-color-muted, #6a6a6a);
}
.rows {
  list-style: none;
  margin: 0;
  padding: 0;
}
.row {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) minmax(0, 1.4fr) 44px;
  align-items: center;
  gap: 12px;
  min-height: var(--row-height);
  padding: 0 8px;
}
/* the first row and every other one after it */
.row:nth-child(odd) {
  background-color: var(--stripe);
}
/* a row that opens Send Email: the pointer, and the admin tables' faint hover; none where a
   click does nothing (DESIGN.md § 15) */
.row.isContactable {
  cursor: pointer;
}
.row.isContactable:hover {
  background-color: var(--row-hover);
}
.dot {
  justify-self: center;
  width: 8px; /* ▶ EDIT HERE: below the capitals' height, as a status dot (Seán, 2026-11-03) */
  height: 8px;
  border-radius: 50%;
  background-color: var(--presence-inactive);
}
.dot.active {
  background-color: var(--presence-active);
}
.dot.recent {
  background-color: var(--presence-recent);
}
.name {
  font-weight: 700;
}
.affiliation {
  color: var(--text-color-muted, #6a6a6a);
}
/* a button that's an icon: global.css's button look reset (CLAUDE.md) */
.emailButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: var(--row-height);
  padding: 0;
  border: none;
  border-radius: 8px;
  box-shadow: none;
  background: none;
  color: var(--email-available);
  cursor: pointer;
}
.emailButton[aria-disabled="true"] {
  color: var(--email-unavailable);
  cursor: not-allowed;
}
.emailButton svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linejoin: round;
}
/* the slash through a not-contactable envelope */
.emailButton .strike {
  stroke-width: 1.8;
  stroke-linecap: round;
}
.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
/* a finger, not a mouse: rows a comfortable tap */
@media (pointer: coarse) {
  .attendees {
    --row-height: 44px;
  }
}
@media (max-width: 600px) {
  .row {
    grid-template-columns: 14px minmax(0, 1fr) 44px;
    grid-template-areas: "dot name contact" ". affiliation contact";
    row-gap: 0;
  }
  .dot {
    grid-area: dot;
  }
  .name {
    grid-area: name;
  }
  .affiliation {
    grid-area: affiliation;
  }
  .contact {
    grid-area: contact;
  }
}
</style>
